import React, {useState, useEffect, useMemo} from "react";
import {Transition} from "@headlessui/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {XMarkIcon} from "@heroicons/react/20/solid";

const ICONS_BY_STATUS = {
  success: {
    Icon: CheckCircleIcon,
    label: "Success",
    iconClass: "text-green-400",
  },
  error: {
    Icon: XCircleIcon,
    label: "Error",
    iconClass: "text-red-400",
  },
  info: {
    Icon: InformationCircleIcon,
    label: "Notice",
    iconClass: "text-blue-400",
  },
};

const Notification = ({
  message,
  status = "success",
  duration = 5000,
  onReset,
  onClose,
}) => {
  const [show, setShow] = useState(false);

  const iconConfig = useMemo(() => {
    return ICONS_BY_STATUS[status] || ICONS_BY_STATUS.success;
  }, [status]);
  const IconComponent = iconConfig.Icon;

  useEffect(() => {
    if (message) {
      setShow(true);
      const timer = setTimeout(() => {
        setShow(false);
        if (onClose) onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);

  if (!message) return null;

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-50"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          enter="transform transition duration-300 ease-out"
          enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:translate-x-2"
          enterTo="opacity-100 translate-y-0 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg dark:bg-white bg-slate-950 shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <IconComponent
                    aria-hidden="true"
                    className={`h-6 w-6 ${iconConfig.iconClass}`}
                  />
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium dark:text-gray-900 text-white">
                    {iconConfig.label}
                  </p>
                  <p className="mt-1 text-sm dark:text-gray-500 text-gray-200">
                    {message}
                  </p>
                </div>
                <div className="ml-4 flex flex-shrink-0 space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShow(false);
                      if (onClose) onClose();
                    }}
                    className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default Notification;
